// Shared helpers for the public event pages (event page, checkout, order confirmation).
// Same APIs and payloads as the mobile app; the backend does all price math.
import { API_BASE_URL } from "../../config/baseUrls";

export const getToken = () => localStorage.getItem("JWTtoken");
export const getUserId = () => localStorage.getItem("userId");

export const signOut = () => {
  localStorage.removeItem("JWTtoken");
  localStorage.removeItem("userId");
};

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

// Sends the login token when there is one. A rejected token (401) signs the user out,
// so the page can carry on as a visitor or send them to login.
export const api = async (path, { method = "GET", body } = {}) => {
  const token = getToken();
  const headers = {};
  if (body) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError("Network problem. Check your connection and try again.", 0);
  }

  let json = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (res.status === 401 && token) signOut();
  if (!res.ok || json?.success === false) {
    const err = json?.error;
    const message =
      (typeof err === "string" && err) ||
      err?.message ||
      json?.message ||
      `Something went wrong (${res.status}).`;
    throw new ApiError(message, res.status);
  }
  return json;
};

export const rupees = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const dayLabel = (isoDate) =>
  new Date(`${isoDate}T12:00:00+05:30`).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "Asia/Kolkata",
  });

// "Valid Sat, 28 Nov" / "Valid Sat, 28 Nov + Sun, 29 Nov" / "Any 1 day: Sat, 28 Nov or Sun, 29 Nov"
export const passDays = (ticketType) => {
  const dates = Array.isArray(ticketType?.validDates) ? ticketType.validDates : [];
  if (dates.length === 0) return null;
  const labels = dates.map(dayLabel);
  const entryDays = ticketType.maxEntryDays || 1;
  if (dates.length > entryDays) return `Any ${entryDays} day: ${labels.join(" or ")}`;
  return `Valid ${labels.join(" + ")}`;
};

export const isSoldOut = (ticketType) =>
  ticketType.ticketBoughtQuantity >= ticketType.ticketMaxQuantity;

export const formatEventDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

// "Sat, 28 Nov, 2026" for one day, "Sat 28 - Sun 29 Nov 2026" when the event runs over
// two or more IST days. Falls back to the start date if the end is missing or not later.
export const formatEventDateRange = (startString, endString) => {
  if (!startString) return null;
  const single = formatEventDate(startString);
  if (!endString) return single;
  const parts = (s) => {
    const p = {};
    new Intl.DateTimeFormat("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    })
      .formatToParts(new Date(s))
      .forEach(({ type, value }) => {
        p[type] = value;
      });
    return p;
  };
  const start = parts(startString);
  const end = parts(endString);
  const monthNo = (p) => String(new Date(`1 ${p.month} 2000`).getMonth()).padStart(2, "0");
  const key = (p) => `${p.year}${monthNo(p)}${String(p.day).padStart(2, "0")}`;
  if (key(end) <= key(start)) return single;
  const sameMonth = start.month === end.month && start.year === end.year;
  const startLabel = sameMonth
    ? `${start.weekday} ${start.day}`
    : `${start.weekday} ${start.day} ${start.month}`;
  return `${startLabel} - ${end.weekday} ${end.day} ${end.month} ${end.year}`;
};

// Campaign tag (NTT DATA page). Kept per tab and per event, so it can't stick to a later purchase.
const CAMPAIGN_KEY = "eventCampaign";
export const setCampaign = (eventId, source, campaign) =>
  sessionStorage.setItem(CAMPAIGN_KEY, JSON.stringify({ eventId, source, campaign }));
export const takeCampaign = (eventId) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CAMPAIGN_KEY) || "null");
    return saved?.eventId === eventId ? saved : null;
  } catch {
    return null;
  }
};
export const clearCampaign = () => {
  sessionStorage.removeItem(CAMPAIGN_KEY);
  localStorage.removeItem("ticket_metadata_source"); // old sticky tag from the previous checkout
};

// Checkout selection survives the trip to the login page
const draftKey = (eventId) => `eventCheckout:${eventId}`;
export const loadDraft = (eventId) => {
  try {
    return JSON.parse(sessionStorage.getItem(draftKey(eventId)) || "null");
  } catch {
    return null;
  }
};
export const saveDraft = (eventId, draft) =>
  sessionStorage.setItem(draftKey(eventId), JSON.stringify(draft));
export const clearDraft = (eventId) => sessionStorage.removeItem(draftKey(eventId));

export const loginUrl = (next) => `/events/login?next=${encodeURIComponent(next)}`;

// Own loader so the shop checkout's useRazorpay hook stays untouched
const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";
export const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    setTimeout(() => resolve(!!window.Razorpay), 15000);
    const existing = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);
    const script = existing || document.createElement("script");
    script.addEventListener("load", () => resolve(true));
    script.addEventListener("error", () => resolve(false));
    if (!existing) {
      script.src = RAZORPAY_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  });
